import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { restaurantApi, getRestaurantApiError } from './restaurantApi';
import {
  ACTIVE_CART_KEY,
  ADDRESS_KEY,
  cartStorageKey,
  DENSITY_KEY,
  hydrateCart,
  isRestaurantOrderable,
  normalizeCartItem,
  readJsonStorage,
  terminalFoodStatuses
} from './restaurantUtils';

const emptyMenu = { categories: [], items: [] };
export const FOOD_ORDERS_POLL_INTERVAL = 10000;

const isCanceled = (error, signal) =>
  signal?.aborted || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

export default function useRestaurantClient({ onOrderCreated }) {
  const [restaurants, setRestaurants] = useState([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [restaurantsError, setRestaurantsError] = useState('');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [lastOrdersUpdatedAt, setLastOrdersUpdatedAt] = useState(null);
  const [selectedOrderId, setSelectedOrderIdState] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [menu, setMenu] = useState(emptyMenu);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [cart, setCart] = useState([]);
  const [pendingRestaurant, setPendingRestaurant] = useState(null);
  const [address, setAddress] = useState(() =>
    readJsonStorage(ADDRESS_KEY, { lat: null, lng: null, address: '', reference: '' })
  );
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [toast, setToast] = useState(null);

  const mountedRef = useRef(false);
  const selectedIdRef = useRef(null);
  const selectedOrderIdRef = useRef(null);
  const ordersRef = useRef([]);
  const menuCache = useRef(new Map());
  const restaurantsRequestRef = useRef({ id: 0, controller: null });
  const ordersRequestRef = useRef({ id: 0, controller: null, inFlight: false });
  const menuRequestRef = useRef({ id: 0, controller: null });

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  const loadRestaurants = useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    restaurantsRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const id = restaurantsRequestRef.current.id + 1;
    restaurantsRequestRef.current = { id, controller };
    if (!silent && mountedRef.current) {
      setRestaurantsLoading(true);
      setRestaurantsError('');
    }
    try {
      const response = await restaurantApi.getRestaurants(controller.signal);
      if (!mountedRef.current || controller.signal.aborted || restaurantsRequestRef.current.id !== id) return null;
      const nextRestaurants = Array.isArray(response.data) ? response.data : [];
      setRestaurants(nextRestaurants);
      setRestaurantsError('');
      return nextRestaurants;
    } catch (error) {
      if (!isCanceled(error, controller.signal) && mountedRef.current && restaurantsRequestRef.current.id === id) {
        setRestaurantsError(getRestaurantApiError(error, 'No pudimos cargar los restaurantes.'));
      }
      return null;
    } finally {
      if (mountedRef.current && restaurantsRequestRef.current.id === id) {
        setRestaurantsLoading(false);
      }
    }
  }, []);

  const loadOrders = useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    const skipIfBusy = options?.skipIfBusy === true;
    if (skipIfBusy && ordersRequestRef.current.inFlight) return null;
    if (!skipIfBusy) ordersRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const id = ordersRequestRef.current.id + 1;
    ordersRequestRef.current = { id, controller, inFlight: true };
    if (!silent && mountedRef.current) {
      setOrdersLoading(true);
      setOrdersError('');
    }
    try {
      const response = await restaurantApi.getOrders(controller.signal);
      if (!mountedRef.current || controller.signal.aborted || ordersRequestRef.current.id !== id) return null;
      const nextOrders = Array.isArray(response.data) ? response.data : [];
      ordersRef.current = nextOrders;
      setOrders(nextOrders);
      setOrdersError('');
      setLastOrdersUpdatedAt(new Date());
      return nextOrders;
    } catch (error) {
      if (!isCanceled(error, controller.signal) && mountedRef.current && ordersRequestRef.current.id === id && !silent) {
        setOrdersError(getRestaurantApiError(error, 'No pudimos cargar tus pedidos.'));
      }
      return null;
    } finally {
      if (ordersRequestRef.current.id === id) {
        ordersRequestRef.current.inFlight = false;
        if (mountedRef.current) setOrdersLoading(false);
      }
    }
  }, []);

  const loadSelectedOrder = useCallback(async (orderId, options = {}) => {
    if (!orderId || ordersRequestRef.current.inFlight) return null;
    const fallbackToList = options?.fallbackToList !== false;
    const controller = new AbortController();
    const id = ordersRequestRef.current.id + 1;
    ordersRequestRef.current = { id, controller, inFlight: true };
    try {
      const response = await restaurantApi.getOrder(orderId, controller.signal);
      if (!mountedRef.current || controller.signal.aborted || ordersRequestRef.current.id !== id) return null;
      const updatedOrder = response.data;
      const nextOrders = ordersRef.current.some((order) => order.id === orderId)
        ? ordersRef.current.map((order) => order.id === orderId ? updatedOrder : order)
        : [updatedOrder, ...ordersRef.current];
      ordersRef.current = nextOrders;
      setOrders(nextOrders);
      setOrdersError('');
      setLastOrdersUpdatedAt(new Date());
      return updatedOrder;
    } catch (error) {
      if (
        fallbackToList
        && !isCanceled(error, controller.signal)
        && mountedRef.current
        && ordersRequestRef.current.id === id
      ) {
        ordersRequestRef.current.inFlight = false;
        return loadOrders({ silent: true, skipIfBusy: true });
      }
      return null;
    } finally {
      if (ordersRequestRef.current.id === id) {
        ordersRequestRef.current.inFlight = false;
      }
    }
  }, [loadOrders]);

  const refreshOrders = useCallback(() => {
    const orderId = selectedOrderIdRef.current;
    const selectedOrder = ordersRef.current.find((order) => order.id === orderId);
    if (orderId && selectedOrder && !terminalFoodStatuses.has(selectedOrder.status)) {
      return loadSelectedOrder(orderId);
    }
    return loadOrders({ silent: true, skipIfBusy: true });
  }, [loadOrders, loadSelectedOrder]);

  useEffect(() => {
    mountedRef.current = true;
    loadRestaurants();
    loadOrders();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshOrders();
    }, FOOD_ORDERS_POLL_INTERVAL);
    return () => {
      mountedRef.current = false;
      window.clearInterval(intervalId);
      restaurantsRequestRef.current.controller?.abort();
      ordersRequestRef.current.controller?.abort();
      menuRequestRef.current.controller?.abort();
    };
  }, [loadOrders, loadRestaurants, refreshOrders]);

  useEffect(() => {
    if (!selectedRestaurant?.id) return;
    if (cart.length) {
      window.localStorage.setItem(cartStorageKey(selectedRestaurant.id), JSON.stringify(cart));
      window.localStorage.setItem(ACTIVE_CART_KEY, selectedRestaurant.id);
    } else {
      window.localStorage.removeItem(cartStorageKey(selectedRestaurant.id));
    }
    if (!cart.length && window.localStorage.getItem(ACTIVE_CART_KEY) === selectedRestaurant.id) {
      window.localStorage.removeItem(ACTIVE_CART_KEY);
    }
  }, [cart, selectedRestaurant]);

  const loadMenu = useCallback(async (restaurant) => {
    menuRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const id = menuRequestRef.current.id + 1;
    menuRequestRef.current = { id, controller };
    const cached = menuCache.current.get(restaurant.id);
    if (cached) {
      if (mountedRef.current && selectedIdRef.current === restaurant.id) {
        setMenu(cached);
        setMenuError('');
        setMenuLoading(false);
      }
      return cached;
    }
    if (mountedRef.current && selectedIdRef.current === restaurant.id) {
      setMenuLoading(true);
      setMenuError('');
    }
    try {
      const response = await restaurantApi.getMenu(restaurant.id, controller.signal);
      if (!mountedRef.current || controller.signal.aborted || menuRequestRef.current.id !== id) return null;
      const nextMenu = response.data || emptyMenu;
      menuCache.current.set(restaurant.id, nextMenu);
      if (selectedIdRef.current === restaurant.id) {
        setMenu(nextMenu);
        setMenuError('');
      }
      return nextMenu;
    } catch (error) {
      if (
        !isCanceled(error, controller.signal)
        && mountedRef.current
        && menuRequestRef.current.id === id
        && selectedIdRef.current === restaurant.id
      ) {
        setMenu(emptyMenu);
        setMenuError(getRestaurantApiError(error, 'No pudimos cargar el menú.'));
      }
      return null;
    } finally {
      if (
        mountedRef.current
        && menuRequestRef.current.id === id
        && selectedIdRef.current === restaurant.id
      ) {
        setMenuLoading(false);
      }
    }
  }, []);

  const openRestaurant = useCallback((restaurant, force = false) => {
    const activeRestaurantId = window.localStorage.getItem(ACTIVE_CART_KEY);
    const storedActiveCart = activeRestaurantId
      ? readJsonStorage(cartStorageKey(activeRestaurantId), [])
      : [];
    if (!force && activeRestaurantId && activeRestaurantId !== restaurant.id && storedActiveCart.length) {
      setPendingRestaurant(restaurant);
      return false;
    }
    selectedIdRef.current = restaurant.id;
    setSelectedRestaurant(restaurant);
    setCart(hydrateCart(readJsonStorage(cartStorageKey(restaurant.id), [])));
    setMenu(emptyMenu);
    loadMenu(restaurant);
    return true;
  }, [loadMenu]);

  const confirmRestaurantChange = useCallback(() => {
    const activeRestaurantId = window.localStorage.getItem(ACTIVE_CART_KEY);
    if (activeRestaurantId) window.localStorage.removeItem(cartStorageKey(activeRestaurantId));
    window.localStorage.removeItem(ACTIVE_CART_KEY);
    const next = pendingRestaurant;
    setPendingRestaurant(null);
    if (next) openRestaurant(next, true);
  }, [openRestaurant, pendingRestaurant]);

  const closeRestaurant = useCallback(() => {
    menuRequestRef.current.controller?.abort();
    menuRequestRef.current.id += 1;
    selectedIdRef.current = null;
    setSelectedRestaurant(null);
    setMenu(emptyMenu);
    setMenuError('');
    setMenuLoading(false);
  }, []);

  const addProduct = useCallback((product, quantity = 1, observation = '') => {
    if (!product.available || !isRestaurantOrderable(selectedRestaurant)) return;
    setCart((current) => {
      const existing = current.find((item) =>
        item.menu_item_id === product.id && item.observation === observation.trim()
      );
      if (existing) {
        return current.map((item) =>
          item === existing ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...current, normalizeCartItem(product, quantity, observation)];
    });
    setToast({ type: 'success', title: 'Agregado al carrito', message: product.name });
  }, [selectedRestaurant]);

  const updateCartItem = useCallback((lineId, product, quantity, observation) => {
    if (!isRestaurantOrderable(selectedRestaurant)) return;
    setCart((current) => {
      const replacement = normalizeCartItem(product, quantity, observation);
      return current
        .filter((item) => item.line_id !== lineId && item.line_id !== replacement.line_id)
        .concat(replacement);
    });
    setToast({ type: 'success', title: 'Producto actualizado', message: product.name });
  }, [selectedRestaurant]);

  const changeQuantity = useCallback((lineId, delta) => {
    setCart((current) => current
      .map((item) => item.line_id === lineId ? { ...item, quantity: item.quantity + delta } : item)
      .filter((item) => item.quantity > 0));
  }, []);

  const saveAddress = useCallback((nextAddress) => {
    setAddress(nextAddress);
    window.localStorage.setItem(ADDRESS_KEY, JSON.stringify(nextAddress));
  }, []);

  const setSelectedOrderId = useCallback((orderId) => {
    selectedOrderIdRef.current = orderId;
    setSelectedOrderIdState(orderId);
  }, []);

  const submitOrder = useCallback(async () => {
    if (
      !selectedRestaurant
      || !isRestaurantOrderable(selectedRestaurant)
      || !cart.length
      || submitting
    ) return null;
    setSubmitting(true);
    setSubmitError('');
    const itemNotes = cart
      .filter((item) => item.observation)
      .map((item) => `${item.name}: ${item.observation}`);
    const combinedNotes = [notes.trim(), ...itemNotes].filter(Boolean).join(' | ').slice(0, 500);
    try {
      const response = await restaurantApi.createOrder({
        restaurant_id: selectedRestaurant.id,
        items: cart.map((item) => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          qty: item.quantity,
          price: item.price
        })),
        dropoff_location: address,
        notes: combinedNotes || null,
        payment_method: 'cash'
      });
      setCart([]);
      window.localStorage.removeItem(cartStorageKey(selectedRestaurant.id));
      window.localStorage.removeItem(ACTIVE_CART_KEY);
      setNotes('');
      await loadOrders();
      onOrderCreated?.(response.data);
      setToast({
        type: 'success',
        title: 'Pedido enviado',
        message: 'El restaurante debe confirmarlo. Ya puedes seguir su estado.'
      });
      return response.data;
    } catch (error) {
      if (mountedRef.current) {
        setSubmitError(getRestaurantApiError(error, 'No pudimos confirmar el pedido.'));
      }
      return null;
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [address, cart, loadOrders, notes, onOrderCreated, selectedRestaurant, submitting]);

  return {
    restaurants,
    restaurantsLoading,
    restaurantsError,
    orders,
    ordersLoading,
    ordersError,
    lastOrdersUpdatedAt,
    selectedOrderId,
    selectedRestaurant,
    menu,
    menuLoading,
    menuError,
    cart,
    pendingRestaurant,
    address,
    notes,
    submitting,
    submitError,
    toast,
    setNotes,
    setToast,
    setPendingRestaurant,
    setSelectedOrderId,
    loadRestaurants,
    loadOrders,
    refreshOrders,
    loadMenu: () => selectedRestaurant && loadMenu(selectedRestaurant),
    openRestaurant,
    closeRestaurant,
    confirmRestaurantChange,
    addProduct,
    updateCartItem,
    changeQuantity,
    saveAddress,
    submitOrder
  };
}

export function useRestaurantDensity() {
  const [density, setDensity] = useState(() => {
    const stored = window.localStorage.getItem(DENSITY_KEY);
    return stored === 'compact' ? 'compact' : 'extended';
  });
  const updateDensity = useCallback((value) => {
    setDensity(value);
    window.localStorage.setItem(DENSITY_KEY, value);
  }, []);
  return useMemo(() => [density, updateDensity], [density, updateDensity]);
}
